// src/lib/resources/types/tools/generator.ts

/**
 * @fileoverview Types for generator tools that create content or assets with advanced configuration options
 * 
 * This module defines the type system for content generators, supporting features like:
 * - Multi-step generation processes
 * - Conditional rendering and logic
 * - Advanced templating with variable substitution
 * - Customizable previews and outputs
 * - Analytics tracking
 * - Dual input modes and flexible validation
 */

import type { BaseTool, SupplementalResourceReference } from '~/lib/resources/types/shared/base'
import type { BaseStep } from '~/lib/resources/types/shared/sections'
import type { BaseInput, ValidationRules } from '~/lib/resources/types/shared/inputs'
import type { BaseToolConfig, UIConfig } from '~/lib/resources/types/shared/configs'
import { type ToolType } from '~/lib/resources/types/shared/enums'

/**
 * Supported template formats for generator inputs and outputs
 */
export type TemplateFormat = 'plain' | 'markdown' | 'html' | 'json' | 'handlebars'

/**
 * Supported output formats for generated content mapped to Schema.org encodingFormat
 */
export type OutputFormat = {
  type: 'text' | 'html' | 'markdown' | 'json' | 'pdf' | 'image' | 'audio' | 'video' | 'spreadsheet' | 'presentation' | 'svg' | 'csv'
  encodingFormat: string // Maps to Schema.org encodingFormat
}

/**
 * Standard MIME type mappings for common output formats
 */
export const OUTPUT_FORMAT_MAPPINGS: Record<OutputFormat['type'], string> = {
  text: 'text/plain',
  html: 'text/html',
  markdown: 'text/markdown',
  json: 'application/json',
  pdf: 'application/pdf',
  image: 'image/png', // Default, can be overridden
  audio: 'audio/mpeg', // Default, can be overridden
  video: 'video/mp4', // Default, can be overridden
  spreadsheet: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  presentation: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  svg: 'image/svg+xml',
  csv: 'text/csv'
}

/**
 * Condition for conditional rendering or logic in templates
 * 
 * @example
 * // Show a field only if user selected "advanced" option
 * condition: {
 *   field: 'userLevel',
 *   operator: 'equals',
 *   value: 'advanced'
 * }
 */
export interface Condition {
  /** Field to evaluate */
  field: string
  /** Comparison operator */
  operator: 'equals' | 'notEquals' | 'contains' | 'notContains' | 'greaterThan' | 'lessThan' | 'greaterThanOrEquals' | 'lessThanOrEquals' | 'isEmpty' | 'isNotEmpty' | 'exists' | 'notExists'
  /** Value to compare against */
  value?: string | number | boolean | string[]
  /** Combine with other conditions */
  and?: Condition[]
  /** Alternative conditions */
  or?: Condition[]
}

/**
 * Requirement condition for field validation
 * Allows for conditional required fields based on other field values
 * 
 * @example
 * // Field is required only when businessType is B2B
 * requiredIf: {
 *   field: 'businessType',
 *   operator: 'equals',
 *   value: 'B2B'
 * }
 */
export interface RequirementCondition extends Condition {
  /** Custom error message to display when validation fails */
  errorMessage?: string
}

/**
 * Input mode configuration for dual input scenarios
 * Allows fields to support multiple interaction methods
 * 
 * @example
 * // Number field with both text input and increment/decrement buttons
 * inputModes: {
 *   primary: 'text',
 *   secondary: 'stepper',
 *   stepperConfig: {
 *     step: 1,
 *     min: 0,
 *     max: 100
 *   }
 * }
 */
export interface InputModeConfig {
  /** Primary input method */
  primary: 'text' | 'number' | 'select' | 'multiselect' | 'textarea' | 'checkbox' | 'radio' | 'file' | 'date' | 'time' | 'datetime' | 'color' | 'range' | 'url' | 'email' | 'tel' | 'password'
  /** Secondary input method (optional) */
  secondary?: 'stepper' | 'slider' | 'toggle' | 'buttons' | 'dropdown' | 'autocomplete' | 'tags'
  /** Configuration for stepper controls */
  stepperConfig?: {
    /** Step increment for stepper buttons */
    step: number
    /** Minimum value for stepper */
    min?: number
    /** Maximum value for stepper */
    max?: number
    /** Custom step sizes for different ranges */
    adaptiveStep?: Array<{
      /** Range start */
      from: number
      /** Range end */
      to: number
      /** Step size for this range */
      step: number
    }>
    /** Enable fast increment on hold */
    fastIncrement?: boolean
    /** Fast increment delay in ms */
    fastIncrementDelay?: number
  }
  /** Configuration for slider controls */
  sliderConfig?: {
    /** Step increment for slider */
    step: number
    /** Minimum value */
    min: number
    /** Maximum value */
    max: number
    /** Show tick marks */
    showTicks?: boolean
    /** Tick mark positions */
    ticks?: number[]
    /** Show value labels */
    showLabels?: boolean
  }
  /** Configuration for toggle controls */
  toggleConfig?: {
    /** Toggle states */
    states: Array<{
      /** State value */
      value: string | number | boolean
      /** State label */
      label: string
      /** State icon */
      icon?: string
    }>
    /** Allow multiple selections */
    multiple?: boolean
  }
  /** Configuration for button group controls */
  buttonConfig?: {
    /** Button options */
    options: Array<{
      /** Option value */
      value: string | number | boolean
      /** Option label */
      label: string
      /** Option icon */
      icon?: string
      /** Option description */
      description?: string
    }>
    /** Allow multiple selections */
    multiple?: boolean
    /** Button size */
    size?: 'small' | 'medium' | 'large'
    /** Button variant */
    variant?: 'default' | 'outline' | 'ghost' | 'solid'
  }
}

/**
 * Enhanced validation constraint with mode-specific application
 * Allows different constraints for different input modes
 */
export interface EnhancedConstraint {
  /** Constraint value */
  value: number
  /** Apply to text input mode */
  applyToText?: boolean
  /** Apply to stepper/slider modes */
  applyToStepper?: boolean
  /** Custom error message */
  message?: string
}

/**
 * Step validation constraint with mode-specific application
 * Separate from UI step controls to allow different validation rules
 */
export interface StepConstraint {
  /** Step value for validation */
  value: number
  /** Apply only to specific input modes */
  applyToModes?: Array<'text' | 'stepper' | 'slider'>
  /** Custom error message */
  message?: string
}

/**
 * Pattern validation constraint with mode-specific application
 */
export interface PatternConstraint {
  /** Regex pattern */
  value: string
  /** Apply only to text-based inputs */
  textOnly?: boolean
  /** Custom error message */
  message?: string
}

/**
 * Custom validation rule definition
 */
export interface CustomValidationRule {
  /** Validation rule name */
  name: string
  /** Validation function */
  fn?: (value: unknown, context?: Record<string, unknown>) => boolean | Promise<boolean>
  /** Error message */
  message: string
  /** Apply only to specific input modes */
  applyToModes?: string[]
  /** Validation parameters */
  params?: Record<string, unknown>
}

/**
 * Enhanced validation configuration that extends base ValidationRules
 * while maintaining backward compatibility
 */
export interface EnhancedValidationConfig extends ValidationRules {
  /** Conditionally required field */
  requiredIf?: RequirementCondition
  /** Enhanced minimum constraints with mode-specific application */
  enhancedMin?: EnhancedConstraint
  /** Enhanced maximum constraints with mode-specific application */
  enhancedMax?: EnhancedConstraint
  /** Step validation (separate from UI step controls) */
  stepConstraint?: StepConstraint
  /** Enhanced pattern validation */
  enhancedPattern?: PatternConstraint
  /** Custom validation rules */
  customRules?: CustomValidationRule[]
}

/**
 * Generator field interface extending BaseInput with template-specific properties
 * Uses intersection type to maintain compatibility while adding enhancements
 */
export interface GeneratorField extends Omit<BaseInput, 'validation'> {
  /** Enhanced validation rules that extend base validation */
  validation?: EnhancedValidationConfig
  /** Template variable name used in templates */
  templateVar?: string
  /** Whether field affects preview rendering */
  affectsPreview?: boolean
  /** Conditional display of this field */
  showIf?: Condition
  /** Field format options */
  format?: {
    /** Input format type */
    type: TemplateFormat
    /** Format-specific options */
    options?: Record<string, unknown>
  }
  /** Default value for the field */
  defaultValue?: unknown
  /** Input mode configuration for dual input scenarios */
  inputModes?: InputModeConfig
  /** Field dependencies */
  dependsOn?: string[]
  /** Analytics tracking */
  analytics?: {
    /** Track when field is viewed */
    trackImpression?: boolean
    /** Track when field is changed */
    trackChange?: boolean
    /** Track input mode switches */
    trackModeSwitch?: boolean
    /** Custom dimensions to include with tracking */
    dimensions?: Record<string, string | number | boolean>
  }
  /** Learning content associated with this field */
  learningContent?: SupplementalResourceReference[]
  /** File input specific properties */
  accept?: string
  /** Maximum file size in bytes */
  maxFileSize?: number
  /** Allowed file types for security */
  allowedFileTypes?: string[]
  /** Security validation for file uploads */
  securityValidation?: {
    /** Scan for malicious content */
    scanForMalware?: boolean
    /** Validate file headers */
    validateFileHeaders?: boolean
    /** Maximum file dimensions for images */
    maxImageDimensions?: {
      width: number
      height: number
    }
    /** Allowed MIME types */
    allowedMimeTypes?: string[]
  }
  /** Field accessibility configuration */
  accessibility?: {
    /** ARIA label */
    ariaLabel?: string
    /** ARIA description */
    ariaDescription?: string
    /** Tab index override */
    tabIndex?: number
    /** Screen reader announcements */
    announcements?: {
      /** Announce on value change */
      onChange?: string
      /** Announce on mode switch */
      onModeSwitch?: string
    }
  }
  /** Field performance optimization */
  performance?: {
    /** Debounce validation in ms */
    debounceValidation?: number
    /** Lazy load field */
    lazyLoad?: boolean
    /** Virtual scrolling for large option lists */
    virtualScrolling?: boolean
  }
}

/**
 * Template helper function definition
 */
export interface TemplateHelper {
  /** Helper name */
  name: string
  /** Helper function */
  fn: (context: unknown, ...args: unknown[]) => string
  /** Helper description for documentation */
  description?: string
  /** Helper parameters */
  parameters?: Array<{
    name: string
    type: string
    description: string
    required?: boolean
  }>
}

/**
 * Generator step interface extending BaseStep with template capabilities
 */
export interface GeneratorStep extends BaseStep {
  /** Input fields for this step */
  fields: GeneratorField[]
  /** Step template */
  template: string
  /** Template engine to use */
  templateEngine?: 'handlebars' | 'mustache' | 'custom'
  /** Custom template helpers */
  templateHelpers?: TemplateHelper[]
  /** Template variables */
  variables?: Record<string, unknown>
  /** Conditional display of this step */
  showIf?: Condition
  /** Step preview configuration */
  preview?: {
    /** Enable live preview */
    enabled: boolean
    /** Preview template */
    template?: string
    /** Preview format */
    format?: TemplateFormat
    /** Preview refresh strategy */
    refreshStrategy?: 'onChange' | 'onBlur' | 'manual' | 'debounced'
    /** Debounce time in ms (if using debounced refresh strategy) */
    debounceTime?: number
    /** Preview update triggers */
    updateTriggers?: {
      /** Fields that trigger preview updates */
      fields?: string[]
      /** Input modes that trigger updates */
      inputModes?: string[]
    }
  }
  /** Step-specific analytics */
  analytics?: {
    /** Track when step is viewed */
    trackImpression?: boolean
    /** Track when step is completed */
    trackCompletion?: boolean
    /** Track field interactions */
    trackFieldInteractions?: boolean
    /** Custom events to track */
    customEvents?: Record<string, { enabled: boolean; data?: Record<string, unknown> }>
    /** Custom dimensions to include with tracking */
    dimensions?: Record<string, string | number | boolean>
  }
  /** Learning objectives for this step */
  objectives?: string[]
  /** Learning content for this step */
  learningContent?: SupplementalResourceReference[]
  /** Step validation configuration */
  stepValidation?: {
    /** Validate all fields before proceeding */
    validateAll?: boolean
    /** Custom step validation function */
    customValidation?: (stepData: Record<string, unknown>) => boolean | Promise<boolean>
    /** Validation error message */
    errorMessage?: string
  }
}

/**
 * Generator output configuration
 */
export interface OutputConfig {
  /** Output format and Schema.org encodingFormat */
  format: OutputFormat
  /** Output template */
  template?: string
  /** Output filename template */
  filename?: string
  /** Custom MIME type override */
  mimeType?: string
  /** Output transformers to apply */
  transformers?: string[]
  /** Conditional output configuration */
  variants?: Array<{
    /** Condition to apply this variant */
    condition: Condition
    /** Format for this variant */
    format: OutputFormat
    /** Template for this variant */
    template: string
    /** Filename for this variant */
    filename?: string
    /** Transformers for this variant */
    transformers?: string[]
  }>
  /** Output post-processing */
  postProcessing?: {
    /** Minify output */
    minify?: boolean
    /** Compress output */
    compress?: boolean
    /** Sanitize output */
    sanitize?: boolean
    /** Custom post-processing functions */
    customProcessors?: string[]
  }
}

/**
 * Generator configuration interface with advanced options
 */
export interface GeneratorConfig extends BaseToolConfig {
  /** Generation steps */
  steps: GeneratorStep[]
  /** Output configuration */
  output: OutputConfig
  /** Global template variables */
  globalVariables?: Record<string, unknown>
  /** Global template helpers */
  globalHelpers?: TemplateHelper[]
  /** What this generator assesses (Schema.org) */
  assesses?: string[]
  /** What this generator teaches (Schema.org) */
  teaches?: string[]
  /** UI preferences */
  ui?: UIConfig & {
    /** Enable preview */
    enablePreview?: boolean
    /** Enable download */
    enableDownload?: boolean
    /** Preview delay in ms */
    previewDelay?: number
    /** Custom CSS for the generator UI */
    customCSS?: string
    /** Layout configuration */
    layout?: 'standard' | 'compact' | 'wizard' | 'split' | 'custom'
    /** Custom components to use */
    customComponents?: Record<string, string>
    /** Input mode preferences */
    inputModePreferences?: {
      /** Default input modes for field types */
      defaults?: Record<string, InputModeConfig>
      /** Allow users to switch input modes */
      allowModeSwitch?: boolean
      /** Remember user's mode preferences */
      rememberPreferences?: boolean
    }
  }
  /** Caching configuration */
  cache?: {
    /** Enable caching of generated content */
    enabled: boolean
    /** Cache duration in seconds */
    duration: number
    /** Cache key generator function name */
    keyGenerator?: string
    /** Cache invalidation triggers */
    invalidationTriggers?: string[]
  }
  /** Analytics configuration */
  analytics?: {
    /** Track generator usage */
    trackUsage?: boolean
    /** Track generated outputs */
    trackOutputs?: boolean
    /** Track input mode usage */
    trackInputModes?: boolean
    /** Custom dimensions to include with all tracking */
    dimensions?: Record<string, string | number | boolean>
  }
  /** Performance configuration */
  performance?: {
    /** Enable lazy loading of steps */
    lazyLoadSteps?: boolean
    /** Enable virtual scrolling for long forms */
    virtualScrolling?: boolean
    /** Debounce form updates in ms */
    debounceUpdates?: number
    /** Maximum concurrent validations */
    maxConcurrentValidations?: number
  }
}

/**
 * Generator tool interface representing a complete generator
 */
export interface Generator extends BaseTool<GeneratorConfig> {
  /** Resource type identifier */
  resourceType: 'tool'
  /** Tool type must be GENERATOR */
  toolType: typeof ToolType.GENERATOR
  /** Generator configuration */
  config: GeneratorConfig
  /**
   * Optional above-the-fold intro paragraph rendered beneath the hero
   * `description` on the generator page. Unlike `description` (a concise,
   * SERP/meta-oriented summary reused across metadata surfaces), `intro` is
   * page-only body copy that frames the platform's primary use-case in a
   * distinct voice — used by landing-page variants to deepen per-variant
   * differentiation (#139) without duplicating the meta description.
   */
  intro?: string
  /**
   * Optional companion-tool callouts rendered below the intro block.
   * Used for crawlable cross-links between high-traffic mockups and
   * plan/measure calculators without competing with `relatedTools` rails.
   *
   * @property {string} href - Destination path.
   * @property {string} title - Link label.
   * @property {string} description - One-line value prop.
   */
  companionLinks?: ReadonlyArray<{
    href: string
    title: string
    description: string
  }>
  /** Optional application category (Schema.org) */
  applicationCategory?: string
  /** Optional application subcategory (Schema.org) */
  applicationSubCategory?: string
  /**
   * Optional feature list (Schema.org).
   *
   * Prefer the `string[]` form for new records — Schema.org's
   * `featureList` property accepts arrays directly, the
   * `.md` exporter renders one bullet per element without
   * parser heuristics, and downstream consumers don't have to
   * re-implement the "split on top-level commas, strip
   * trailing 'and '" parsing every time they want the bullet
   * shape. The legacy `string` form (comma-separated, parens
   * for nested clauses) is still accepted for back-compat with
   * older data records; the export pipeline normalises both
   * via `parseFeatures`.
   */
  featureList?: string | string[]
  /** Optional assesses (Schema.org) */
  assesses?: string
  /** Optional teaches (Schema.org) */
  teaches?: string
  /** Optional interactivity type (Schema.org) */
  interactivityType?: string
  /** Optional software requirements (Schema.org) */
  softwareRequirements?: string
  /** Optional browser requirements (Schema.org) */
  browserRequirements?: string
  /**
   * Optional umbrella-generator slug whose React component / `config`
   * delivers the real interactive surface for this record. Used by
   * landing-page variants that re-use an existing umbrella generator's
   * UI (see `data/generators/landingVariants.ts`) — their own `config`
   * is a deliberately minimal stub that exists only to satisfy the
   * type checker and to give the tool-index card stub something to
   * render. Consumers that want to introspect the real capability set
   * (field count, available steps, fields available for form prefill,
   * etc.) should follow `delegatedConfigOf` to the umbrella record and
   * read THAT record's `config` instead. Today's `.md` export and
   * tool-index rendering do not read `config` for variants, so this
   * field is informational; future consumers introspecting `config`
   * SHOULD honor it.
   */
  delegatedConfigOf?: string
}

// `isGenerator` lives in `types/shared/type-guards.ts` (single canonical home;
// A5/D1 dedup). Import it from there.

/**
 * Utility type for extracting field values from a generator step
 * Uses proper constraint to ensure field names are strings
 */
export type StepFieldValues<T extends GeneratorStep> = Record<T['fields'][number] extends { name: infer N } 
    ? N extends string 
      ? N 
      : never 
    : never, unknown>

/**
 * Utility type for generator field with specific input mode
 */
export type GeneratorFieldWithMode<T extends InputModeConfig['primary']> = GeneratorField & {
  inputModes: InputModeConfig & {
    primary: T
  }
}

/**
 * Type helper for validation function context
 */
export type ValidationContext = {
  /** Current field value */
  value: unknown
  /** All field values in current step */
  stepValues: Record<string, unknown>
  /** All values from previous steps */
  previousStepValues: Record<string, unknown>
  /** Current input mode */
  inputMode: string
  /** Field configuration */
  field: GeneratorField
}

/**
 * Backward compatibility helper types for migration
 */
export type LegacyValidationConfig = ValidationRules

/**
 * Migration helper to convert legacy validation to enhanced validation
 */
export function migrateLegacyValidation(legacy: LegacyValidationConfig): EnhancedValidationConfig {
  const enhanced: EnhancedValidationConfig = { ...legacy }
  
  // Convert simple min/max to enhanced constraints if they exist
  if (typeof legacy.min === 'number') {
    enhanced.enhancedMin = {
      value: legacy.min,
      applyToText: true,
      applyToStepper: true
    }
  }
  
  if (typeof legacy.max === 'number') {
    enhanced.enhancedMax = {
      value: legacy.max,
      applyToText: true,
      applyToStepper: true
    }
  }
  
  return enhanced
}

/**
 * Type predicate to check if validation config is enhanced
 */
export function isEnhancedValidation(
  validation: ValidationRules | EnhancedValidationConfig
): validation is EnhancedValidationConfig {
  return (
    'enhancedMin' in validation ||
    'enhancedMax' in validation ||
    'stepConstraint' in validation ||
    'enhancedPattern' in validation ||
    'customRules' in validation ||
    'requiredIf' in validation
  )
}
